import { Provider } from '@angular/core';
import { Store } from '@ngxs/store';
import { AuthState } from 'src/app/store/auth/auth.state';
import { AuthService } from '../auth/auth.service';
import { AGENT_GATEWAY_AUTH, AgentGatewayAuth } from './agent-gateway-auth';
import { CopilotMcpConfigService } from './copilot-mcp-config.service';
import { AgentGatewayContext } from './copilot.types';

const TOKEN_RETRY_DELAYS_MS = [0, 250, 500];

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Resolve a profile-scoped bearer with short retries.
 * Copilot often opens before AuthState / scoped-token cache is warm; a single
 * failed fetch used to send chat with no Authorization → false "session expired".
 */
async function resolveProfileToken(auth: AuthService): Promise<string | undefined> {
  for (const delayMs of TOKEN_RETRY_DELAYS_MS) {
    if (delayMs > 0) {
      await sleep(delayMs);
    }
    try {
      let token = await auth.getCachedScopedToken('profile');
      if (!token) {
        token = await auth.getScopedToken('profile');
      }
      const trimmed = typeof token === 'string' ? token.trim() : '';
      if (trimmed) {
        return trimmed;
      }
    } catch {
      // Retry — session may still be hydrating.
    }
  }
  return undefined;
}

/**
 * Wires {@link AGENT_GATEWAY_AUTH} to the real {@link AuthService}.
 *
 * Token strategy: reuse the SAME short-lived scoped token app-web already mints for its
 * other authenticated bearer calls — the `profile` scope (cached ~1 min, cleared on logout),
 * obtained through the cookie session in v2. `getTokenHeader()` picks the correct scheme
 * (`Bearer` in v2, `JWT` in v1). No new API-server scope is introduced. The gateway forwards
 * this `Authorization` header verbatim to api-server, so tool calls run under the human's RBAC.
 *
 * When a personal MCP key has been provisioned it is forwarded as `x-td-mcp-key` so the
 * gateway can read live workspace stats via MCP (the profile token alone cannot).
 *
 * Company/user ids: prefer explicit context; otherwise fall back to AuthState so lightweight
 * GETs (e.g. `/agent/usage`, feedback) still satisfy gateway session introspection.
 *
 * Registered in `AppModule` so root-injector services (e.g. `InsightsFeedService`) can
 * resolve auth without depending on lazy-loaded feature modules.
 */
export const agentGatewayAuthProvider: Provider = {
  provide: AGENT_GATEWAY_AUTH,
  useFactory: (
    auth: AuthService,
    mcpConfig: CopilotMcpConfigService,
    store: Store,
  ): AgentGatewayAuth => ({
    async getAuthHeaders(context: AgentGatewayContext): Promise<Record<string, string>> {
      const headers: Record<string, string> = {};

      const authCompany = store.selectSnapshot(AuthState.company);
      const authUser = store.selectSnapshot(AuthState.user);
      const userId = context.userId ?? authUser?.id;
      const companyId = context.companyId ?? authCompany?.id;

      if (userId) {
        headers['x-td-user-id'] = userId;
      }
      if (companyId) {
        headers['x-td-company-id'] = companyId;
      }

      const token = await resolveProfileToken(auth);
      if (token) {
        Object.assign(headers, auth.getTokenHeader(token));
      }

      const mcpKey = mcpConfig.getApiKey();
      if (mcpKey) {
        headers['x-td-mcp-key'] = mcpKey;
      }

      return headers;
    },
  }),
  deps: [AuthService, CopilotMcpConfigService, Store],
};
